import { defineAbilityFor } from "@/lib/casl/ability";
import type { AppAbility, DefineAbilityForUser } from "@/lib/casl/types";
import type { UserRole } from "@/schemas/User";
import { useUserStore } from "@/store/useUserStore";
import { createContextualCan } from "@casl/react";
import { type PropsWithChildren, createContext, useMemo } from "react";

export const AbilityContext = createContext<AppAbility>(defineAbilityFor(null));

export const Can = createContextualCan(AbilityContext.Consumer);

export function AbilityProvider({ children }: PropsWithChildren) {
  const id = useUserStore((s) => s.id);
  const status = useUserStore((s) => s.status);
  const role = useUserStore((s) => s.role) as UserRole | null;
  const type = useUserStore((s) => s.type);
  const attributions = useUserStore((s) => s.attributions)?.map(
    ({ value }) => value,
  );
  const organizationId = useUserStore((s) => s.organization?.id);

  const ability = useMemo(() => {
    // status fica fora: null é o valor normal de um usuário ativo
    // (só recebe valor em casos como awaiting_activation / new_email)
    const requiredData = { id, role, type, attributions };

    if (Object.values(requiredData).some((value) => value == null)) {
      return defineAbilityFor(null);
    }

    return defineAbilityFor({
      ...requiredData,
      status,
      organizationId,
    } as DefineAbilityForUser);
  }, [id, status, role, type, attributions, organizationId]);

  return (
    <AbilityContext.Provider value={ability}>
      {children}
    </AbilityContext.Provider>
  );
}
